/**
 * Sotto-componenti di AthleteTestingViewTable estratti per rispettare il limite 600 righe (CLAUDE.md).
 * - TestEntryRow: riga singolo test in modalità entry (sez.7 / sez.7.3)
 * - AnalyticsModeCards: modalità analytics con grafico per test (sez.7)
 */
import { FocusEvent, Dispatch, SetStateAction } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import cx from 'classnames';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { TEST_DIRECTION } from '../../enums/testing';
import { calculateTeamAverage, calculatePercentile, calculateTrendSlope, normalizeTestValue } from '../../utils/testing-calculations';
import styles from './AthleteTestingViewTable.module.scss';

type TimeframeKey = '1m' | '3m' | '6m' | 'season' | 'all';

const timeframeLabels: Record<TimeframeKey, string> = {
  '1m': '1 Mese',
  '3m': '3 Mesi',
  '6m': '6 Mesi',
  'season': 'Stagione',
  'all': 'Tutto',
};

// ── Helper: colori/icone delta ──
function getDeltaColor(delta: number | null, higherIsBetter: boolean): string {
  if (delta === null) return '#4d4d4d';
  const isImprovement = higherIsBetter ? delta > 0 : delta < 0;
  return isImprovement ? '#27ae60' : '#f57c73';
}

function getDeltaIcon(delta: number | null, higherIsBetter: boolean) {
  if (delta === null || Math.abs(delta) < 0.01) return <Minus style={{ width: 14, height: 14 }} />;
  const isImprovement = higherIsBetter ? delta > 0 : delta < 0;
  return isImprovement
    ? <TrendingUp style={{ width: 14, height: 14 }} />
    : <TrendingDown style={{ width: 14, height: 14 }} />;
}

// ── Helper: range data per timeframe ──
export function getDateRange(timeframe: TimeframeKey): { startDate: string; endDate: string } {
  const today = new Date();
  let startDate = new Date(today);
  switch (timeframe) {
    case '1m': startDate.setMonth(today.getMonth() - 1); break;
    case '3m': startDate.setMonth(today.getMonth() - 3); break;
    case '6m': startDate.setMonth(today.getMonth() - 6); break;
    case 'season':
      startDate = new Date(today.getFullYear(), 8, 1);
      if (today.getMonth() < 8) startDate.setFullYear(today.getFullYear() - 1);
      break;
    case 'all': startDate = new Date('2020-01-01'); break;
  }
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TestEntryRow — riga entry per singolo test (sez.7 / sez.7.3)
// ─────────────────────────────────────────────────────────────────────────────
export interface TestEntryRowProps {
  test: ITestDefinition;
  selectedDate: string;
  getResult: (testId: string) => ITestResult | undefined;
  getAllResults: (testId: string, tf?: { startDate: string; endDate: string }) => ITestResult[];
  getTeamValues: (testId: string) => number[];
  getMetrics: (test: ITestDefinition) => string[];
  getDisplayScalar: (test: ITestDefinition) => string;
  getDisplayMetric: (test: ITestDefinition, metric: string) => string;
  setLocalScalar: (testId: string, val: string) => void;
  setLocalMetric: (testId: string, metric: string, val: string) => void;
  isSaving: boolean;
  handleScalarBlur: (test: ITestDefinition) => void;
  handleMetricBlur: (test: ITestDefinition, e?: FocusEvent<HTMLInputElement>) => void;
  /** Risolve la label personalizzata per l'unità del test; fallback all'unità grezza */
  getUnitLabel?: (test: ITestDefinition, unit: string) => string;
}

export function TestEntryRow({
  test,
  selectedDate,
  getResult,
  getAllResults,
  getTeamValues,
  getMetrics,
  getDisplayScalar,
  getDisplayMetric,
  setLocalScalar,
  setLocalMetric,
  isSaving,
  handleScalarBlur,
  handleMetricBlur,
  getUnitLabel,
}: TestEntryRowProps) {
  // Risolve label personalizzata o restituisce l'unità grezza come fallback
  const resolveLabel = (unit: string) =>
    getUnitLabel ? getUnitLabel(test, unit) : unit;
  const currentResult = getResult(test.id);
  const allResults = getAllResults(test.id);
  const teamValues = getTeamValues(test.id);
  const metrics = getMetrics(test);
  const higherIsBetter = test.direction === TEST_DIRECTION.HIGHER_BETTER;
  const primaryValue = currentResult?.value ?? null;

  // Percentile / media team (sez.7) — semantica canonica backend via
  // @leopart/calc: il valore dell'atleta viene normalizzato all'unità
  // canonica del test (i teamValues arrivano già normalizzati da
  // getTeamValues); se la conversione non è supportata si usa il valore
  // grezzo, come fa il backend in testing.service. Fix unità kg/lb VOLUTO.
  const normalizedPrimaryValue =
    primaryValue !== null
      ? normalizeTestValue(primaryValue, currentResult?.unit, test.defaultUnit) ??
        primaryValue
      : null;
  const percentile =
    normalizedPrimaryValue !== null && teamValues.length > 1
      ? calculatePercentile(normalizedPrimaryValue, teamValues, test.direction)
      : null;
  const teamAvg = teamValues.length > 0 ? calculateTeamAverage(teamValues) : null;

  // Delta vs precedente (sez.7)
  let deltaPrevious: number | null = null;
  if (currentResult && allResults.length > 1) {
    const prev = allResults.find((r) => !r.date.startsWith(selectedDate));
    if (prev) deltaPrevious = (currentResult.value ?? 0) - (prev.value ?? 0);
  }

  // Delta vs baseline (sez.7)
  let deltaBaseline: number | null = null;
  if (currentResult && allResults.length > 0) {
    const baseline = allResults[allResults.length - 1];
    deltaBaseline = (currentResult.value ?? 0) - (baseline.value ?? 0);
  }

  // Trend / sparkline (sez.7)
  const chronologicalValues = [...allResults].reverse().map((r) => r.value ?? 0);
  const trendResult = calculateTrendSlope(chronologicalValues, test.direction);
  const trend: 'improving' | 'declining' | 'stable' =
    trendResult.trend === 'flat' ? 'stable' : trendResult.trend;
  const sparklineData = allResults.slice(0, 6).reverse().map((r) => r.value ?? 0);

  // Ultima sessione diversa dalla data corrente
  const lastResult = allResults.find((r) => !r.date.startsWith(selectedDate)) ?? null;
  const decimalStep = test.defaultUnit === '%' ? '0.1' : test.defaultUnit === 's' ? '0.01' : '1';

  return (
    <tr className={styles.tableRow}>
      <td className={styles.tdName}>
        <p className={styles.testName}>{test.name}</p>
      </td>

      {/* Cella VALORE: scalare o multi-metrica impilata (sez.7 / sez.7.3) */}
      <td className={styles.tdValue}>
        {metrics.length > 0 ? (
          <div className={styles.valueCell}>
            <div className={styles.metricStack}>
              {metrics.map((m) => (
                <label key={m} className={styles.metricRow}>
                  <span className={styles.metricLabel}>{m}</span>
                  <input
                    type="number"
                    step={decimalStep}
                    inputMode="decimal"
                    data-cell={test.id}
                    value={getDisplayMetric(test, m)}
                    onChange={(e) => setLocalMetric(test.id, m, e.target.value)}
                    onBlur={(e) => handleMetricBlur(test, e)}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    placeholder="-"
                    className={cx(styles.valueInput, isSaving && styles.valueInputSaving)}
                    disabled={isSaving}
                  />
                </label>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.valueCell}>
            <div className={styles.valueInputGroup}>
              <input
                type="number"
                step={decimalStep}
                value={getDisplayScalar(test)}
                onChange={(e) => setLocalScalar(test.id, e.target.value.replace(/[^0-9.,]/g, ''))}
                onBlur={() => handleScalarBlur(test)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                placeholder="-"
                className={cx(styles.valueInput, isSaving && styles.valueInputSaving)}
                disabled={isSaving}
              />
              {/* Mostra label personalizzata se configurata, altrimenti unità grezza */}
              <p className={styles.valueUnit}>{resolveLabel(test.defaultUnit)}</p>
            </div>
          </div>
        )}
      </td>

      {/* ULTIMA */}
      <td className={styles.tdCenter}>
        {lastResult ? (() => {
          const d = new Date(lastResult.date);
          const fmt = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
          const v = lastResult.value ?? 0;
          return (
            <div className={styles.lastValueGroup}>
              <p className={styles.lastValueNum}>
                {v.toFixed(test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 0)}
              </p>
              <div className={styles.lastValueMeta}>
                {/* Label personalizzata anche nella colonna ULTIMA */}
                <p className={styles.lastValueUnit}>{resolveLabel(test.defaultUnit)}</p>
                <div className={styles.infoWrapper}>
                  <Info className={styles.infoIcon} />
                  <div className={styles.infoTooltip}>{fmt}</div>
                </div>
              </div>
            </div>
          );
        })() : <p className={styles.emptyCell}>-</p>}
      </td>

      {/* MEDIA TEAM */}
      <td className={styles.tdCenter}>
        {teamAvg !== null ? (
          <p className={styles.lastValueNum}>
            {teamAvg.toFixed(test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 1)}
            {/* Label personalizzata anche nella colonna MEDIA TEAM */}
            <span className={styles.valueUnit}> {resolveLabel(test.defaultUnit)}</span>
          </p>
        ) : <p className={styles.emptyCell}>-</p>}
      </td>

      {/* PERCENTILE */}
      <td className={styles.tdCenter}>
        {percentile !== null && percentile > 0 ? (
          <div className={styles.percentileGroup}>
            <p className={styles.percentileNum}>{percentile}°</p>
            <p className={styles.percentileLabel}>percentile</p>
          </div>
        ) : <p className={styles.emptyCell}>-</p>}
      </td>

      {/* Δ PREC. */}
      <td className={styles.tdCenter}>
        {deltaPrevious !== null ? (
          <div className={styles.deltaCell} style={{ color: getDeltaColor(deltaPrevious, higherIsBetter) }}>
            {getDeltaIcon(deltaPrevious, higherIsBetter)}
            <p className={styles.deltaNum}>
              {deltaPrevious > 0 ? '+' : ''}
              {deltaPrevious.toFixed(test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 1)}
            </p>
          </div>
        ) : <p className={styles.emptyCell}>-</p>}
      </td>

      {/* Δ BASELINE */}
      <td className={styles.tdCenter}>
        {deltaBaseline !== null && deltaBaseline !== 0 ? (
          <div className={styles.deltaCell} style={{ color: getDeltaColor(deltaBaseline, higherIsBetter) }}>
            {getDeltaIcon(deltaBaseline, higherIsBetter)}
            <p className={styles.deltaNum}>
              {deltaBaseline > 0 ? '+' : ''}
              {deltaBaseline.toFixed(test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 1)}
            </p>
          </div>
        ) : <p className={styles.baselineLabel}>baseline</p>}
      </td>

      {/* TREND sparkline */}
      <td className={styles.tdCenter}>
        {sparklineData.length >= 2 ? (
          <div className={styles.trendCell}>
            <svg width="60" height="24" viewBox="0 0 60 24">
              <polyline
                fill="none"
                stroke={trend === 'improving' ? '#27ae60' : trend === 'declining' ? '#f57c73' : '#b3b3b3'}
                strokeWidth="2"
                points={sparklineData.map((val, idx) => {
                  const minVal = Math.min(...sparklineData);
                  const maxVal = Math.max(...sparklineData);
                  const range = maxVal - minVal || 1;
                  const x = (idx / (sparklineData.length - 1)) * 55 + 2;
                  const y = 22 - ((val - minVal) / range) * 20;
                  return `${x},${y}`;
                }).join(' ')}
              />
            </svg>
            {trend === 'improving' && <TrendingUp style={{ width: 12, height: 12, color: '#27ae60' }} />}
            {trend === 'declining' && <TrendingDown style={{ width: 12, height: 12, color: '#f57c73' }} />}
            {trend === 'stable' && <Minus style={{ width: 12, height: 12, color: '#b3b3b3' }} />}
          </div>
        ) : <p className={styles.emptyCell}>-</p>}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AnalyticsModeCards — modalità analytics con grafico per test (sez.7)
// ─────────────────────────────────────────────────────────────────────────────
export interface AnalyticsModeCardsProps {
  visibleCategoryTests: ITestDefinition[];
  getAllResults: (testId: string, tf?: { startDate: string; endDate: string }) => ITestResult[];
  chartTimeframes: Record<string, TimeframeKey>;
  setChartTimeframes: Dispatch<SetStateAction<Record<string, TimeframeKey>>>;
}

export function AnalyticsModeCards({
  visibleCategoryTests,
  getAllResults,
  chartTimeframes,
  setChartTimeframes,
}: AnalyticsModeCardsProps) {
  return (
    <div className={styles.analyticsRoot}>
      {visibleCategoryTests.map((test) => {
        const timeframe = chartTimeframes[test.id] || 'all';
        const { startDate, endDate } = getDateRange(timeframe);
        const allResultsFiltered = getAllResults(test.id, { startDate, endDate });
        const allResultsUnfiltered = getAllResults(test.id);

        if (allResultsUnfiltered.length === 0) return null;

        const chartData = [...allResultsFiltered].reverse().map((r) => ({
          date: new Date(r.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: '2-digit' }),
          value: r.value ?? 0,
        }));

        return (
          <div key={test.id} className={styles.analyticsCard}>
            <div className={styles.analyticsCardHeader}>
              <div>
                <h3 className={styles.analyticsCardTitle}>{test.name}</h3>
                <p className={styles.analyticsCardSubtitle}>
                  Ultimo:{' '}
                  {(allResultsUnfiltered[0].value ?? 0).toFixed(
                    test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 0
                  )}{' '}
                  {test.defaultUnit}
                </p>
              </div>
              <div className={styles.analyticsRight}>
                <div className={styles.timeframeChips}>
                  {(Object.keys(timeframeLabels) as TimeframeKey[]).map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setChartTimeframes((prev) => ({ ...prev, [test.id]: tf }))}
                      className={cx(
                        styles.timeframeChip,
                        (chartTimeframes[test.id] || 'all') === tf && styles.timeframeChipActive
                      )}
                    >
                      {timeframeLabels[tf]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {chartData.length > 1 ? (() => {
              const dataValues = chartData.map((d) => d.value);
              const domainMin = Math.min(...dataValues) * 0.95;
              const domainMax = Math.max(...dataValues) * 1.05;
              return (
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                        tickLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={35}
                      />
                      <YAxis
                        domain={[domainMin, domainMax]}
                        tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                        tickLine={false}
                        axisLine={false}
                        width={45}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--c-card-bg)',
                          border: '1px solid #e7e7e7',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontFamily: "'Satoshi:Medium',sans-serif",
                        }}
                        formatter={(value: any) => [
                          Number(value).toFixed(
                            test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 0
                          ) + ' ' + test.defaultUnit,
                          test.name,
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#013344"
                        strokeWidth={3}
                        dot={{ fill: '#013344', r: 5, strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })() : (
              <div className={styles.emptyChart}>
                <div className={styles.emptyChartInner}>
                  <p className={styles.emptyChartTitle}>Nessun dato nel periodo selezionato</p>
                  <p className={styles.emptyChartSubtitle}>Seleziona un periodo più ampio per visualizzare il grafico</p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
